import { useEffect, useRef } from "react";
import { usePub } from "../../helpers/hooks/pubSub";
import { getEnvValueFor } from "../../helpers/utils";
import usePusher from "../../helpers/hooks/usePusher";
import { createConsumer, Consumer, Subscription } from "@rails/actioncable";

interface PendingResultWaitEventsProps {
  children: React.ReactNode;
}

const PendingResultWaitEvents = ({
  children
}: PendingResultWaitEventsProps) => {
  const publishMessage = usePub();
  const { pusherInstance } = usePusher();
  const pusherChannel = useRef<any>(null);
  const actionCableConsumer = useRef<Consumer | null>(null);
  const actionCableSubscription = useRef<Subscription | null>(null);
  const channelName = "query_results_updates";

  // Check if we should use Pusher or Action Cable
  const usePusherForResults =
    getEnvValueFor("QUERY_RESULT_PUSH_USE_PUSHER") === "true";

  useEffect(() => {
    if (usePusherForResults && pusherInstance) {
      // Use Pusher
      pusherChannel.current = pusherInstance.subscribe(channelName);
      pusherChannel.current.bind("result_ready", (data: { result_id: string }) => {
        publishMessage(`pending_result_ready_${data.result_id}`, data);
      });

      return () => {
        if (pusherChannel.current) {
          pusherInstance.unsubscribe(channelName);
          pusherChannel.current = null;
        }
      };
    } else if (!usePusherForResults) {
      // Use Action Cable
      actionCableConsumer.current = createConsumer();

      // Subscribe to query results channel
      actionCableSubscription.current = actionCableConsumer.current.subscriptions.create(
        "QueryResultsChannel",
        {
          received: (data: { result_id: string }) => {
            publishMessage(`pending_result_ready_${data.result_id}`, data);
          }
        }
      );

      return () => {
        // Cleanup ActionCable subscription and consumer
        // Note: This race condition warning may still appear in server logs occasionally
        // because the connection might close between our check and the unsubscribe call.
        // This is expected behavior in distributed systems.
        try {
          if (actionCableSubscription.current) {
            // Check if consumer is still connected before attempting to unsubscribe
            const consumer = actionCableConsumer.current as any;
            const isConnected = consumer?.connection?.isOpen?.();
            
            if (isConnected) {
              // Only unsubscribe if connection is still open
              actionCableSubscription.current.unsubscribe();
            }
            actionCableSubscription.current = null;
          }
        } catch (error) {
          // Silently ignore errors if connection is already closed (expected behavior)
          // This catch block handles the race condition where connection closes
          // between our isOpen check and the unsubscribe call
        }

        try {
          if (actionCableConsumer.current) {
            actionCableConsumer.current.disconnect();
            actionCableConsumer.current = null;
          }
        } catch (error) {
          // Silently ignore errors if connection is already closed (expected behavior)
        }
      };
    }
  }, [usePusherForResults, pusherInstance, publishMessage]);

  return <>{children}</>;
};

export default PendingResultWaitEvents;
